<script
  lang="ts"
  setup
  generic="ColumnData extends CustomColumn[] | Readonly<CustomColumn[]>, RowData"
>
/**
 * BaseGrid
 *
 * 기존 ev-grid를 사용하던 컴포넌트를 ModernGrid 기반의 BaseGridAdapter로 교체.
 * Props/Emit 인터페이스는 동일하게 유지하여 기존 사용처에서 변경 없이 동작합니다.
 */
import { useTemplateRef, useSlots, computed, type ComponentPublicInstance } from 'vue';
import BaseGridAdapter from '@/common/components/molecules/baseGridAdapter/BaseGridAdapter.vue';
import { type Emit, type Props } from '@/common/components/molecules/grid/baseGrid.setup';
import { baseGridOption } from '@/common/utils/define';
import type { CustomColumn } from '@/common/utils/types';
import type { ModernGridExposed } from '../modernGrid/modernGrid.types';

// predefined 슬롯 목록 (명시적으로 처리되는 슬롯)
const PREDEFINED_SLOTS = new Set([
  'toolbar',
  'toolbarPrefix',
  'toolbarPostfix',
  'subToolbar',
  'topToolbar',
  'customHeader',
  'rowDetail',
  'tooltip-content',
]);

const slots = useSlots();

// custom cell 슬롯만 필터링 (predefined 슬롯 제외)
const customCellSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !PREDEFINED_SLOTS.has(name)),
);

const props = withDefaults(defineProps<Props<ColumnData, RowData>>(), {
  type: 'page',
  disabledRows: () => [],
  selectedRows: () => [],
  checkedRows: () => [],
  uncheckable: () => [],
  searchWord: undefined,
  option: () => structuredClone(baseGridOption),
  searchBarOption: () => ({
    use: false,
    mode: 'full',
    placeholder: '',
    useClientFilter: false,
    useMergedToolbar: false,
    filterSearch: {
      use: false,
    },
  }),
  tooltipOption: 'list',
  pagination: () => ({
    use: false,
    useClient: false,
    align: 'right',
    showSetting: false,
    showPageInfo: false,
    perData: 20,
    items: [20, 30, 40, 50],
    total: 0,
  }),
  exportExcelOption: () => ({}),
  hasDynamicColumns: false,
  customClass: () => ({}),
});

const emit = defineEmits<Emit<ColumnData, RowData>>();

// BaseGridAdapter ref
const adapterRef = useTemplateRef<ComponentPublicInstance & ModernGridExposed>('adapterRef');

// expose 함수들 포워딩
defineExpose({
  forceResetEvGrid: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.forceResetEvGrid?.();
  },
  get table() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (adapterRef.value as any)?.table;
  },
  syncGridState: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.syncGridState?.();
  },
  syncRowSelection: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.syncRowSelection?.();
  },
  exportExcel: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.exportExcel?.();
  },
  resetSort: async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (adapterRef.value as any)?.resetSort?.();
  },
});
</script>

<template>
  <base-grid-adapter
    ref="adapterRef"
    :columns="props.columns"
    :rows="props.rows"
    :column-env-key="props.columnEnvKey"
    :type="props.type"
    :disabled-rows="props.disabledRows"
    :selected-rows="props.selectedRows"
    :uncheckable="props.uncheckable"
    :checked-rows="props.checkedRows"
    :search-word="props.searchWord"
    :option="props.option"
    :search-bar-option="props.searchBarOption"
    :tooltip-option="props.tooltipOption"
    :shown-chip-count="props.shownChipCount"
    :pagination="props.pagination"
    :export-excel-option="props.exportExcelOption"
    :expanded="props.expanded"
    :has-dynamic-columns="props.hasDynamicColumns"
    :ref-obj="props.refObj"
    :inner-class="props.innerClass"
    :custom-class="props.customClass"
    :is-tree-mode="false"
    @click-cell="(v) => emit('click-cell', v)"
    @click-cell-chip="(v) => emit('click-cell-chip', v)"
    @click-cell-chip-more="(v) => emit('click-cell-chip-more', v)"
    @page-change="(v) => emit('page-change', v)"
    @on-search="(v) => emit('on-search', v)"
    @export-to-excel-from-server="(v) => emit('export-to-excel-from-server', v)"
    @update:selected-rows="(v) => emit('update:selected-rows', v)"
    @update:checked-rows="(v) => emit('update:checked-rows', v)"
    @update:ref-obj="(v) => emit('update:ref-obj', v)"
    @update:search-word="(v) => emit('update:search-word', v)"
    @update:columns="(v) => emit('update:columns', v)"
    @check-row="(isCheck, rowIndex, row) => emit('check-row', isCheck, rowIndex, row)"
    @check-all="(isCheck, rows) => emit('check-all', isCheck, rows)"
    @click-row="(v) => emit('click-row', v)"
    @update:expanded="(v) => emit('update:expanded', v)"
    @dblclick-row="(v) => emit('dblclick-row', v)"
    @sort-column="(v) => emit('sortColumn', v)"
    @change-column-info="(v) => emit('change-column-info', v)"
    @change-filter-search-result="(v) => emit('changeFilterSearchResult', v)"
  >
    <!-- toolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbar"
      #toolbar
    >
      <slot name="toolbar" />
    </template>

    <!-- toolbarPrefix 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbarPrefix"
      #toolbarPrefix
    >
      <slot name="toolbarPrefix" />
    </template>

    <!-- toolbarPostfix 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbarPostfix"
      #toolbarPostfix
    >
      <slot name="toolbarPostfix" />
    </template>

    <!-- subToolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.subToolbar"
      #subToolbar
    >
      <slot name="subToolbar" />
    </template>

    <!-- topToolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.topToolbar"
      #topToolbar
    >
      <slot name="topToolbar" />
    </template>

    <!-- customHeader 슬롯 포워딩 -->
    <template
      v-if="$slots.customHeader"
      #customHeader="{ item }"
    >
      <slot
        name="customHeader"
        :item="item"
      />
    </template>

    <!-- rowDetail 슬롯 포워딩 -->
    <template
      v-if="$slots.rowDetail"
      #rowDetail="{ item }"
    >
      <slot
        name="rowDetail"
        :item="item"
      />
    </template>

    <!-- tooltip-content 슬롯 포워딩 -->
    <template
      v-if="$slots['tooltip-content']"
      #tooltip-content
    >
      <slot name="tooltip-content" />
    </template>

    <!-- 동적 커스텀 셀 슬롯 포워딩 (predefined 슬롯 제외) -->
    <template
      v-for="slotName in customCellSlotNames"
      :key="slotName"
      #[slotName]="slotProps"
    >
      <slot
        :name="slotName"
        v-bind="slotProps || {}"
      />
    </template>
  </base-grid-adapter>
</template>
